import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';

const CourtManager = ({ registrations = [], eventData = {}, onSaveAssignments, onInitializeMatches }) => {
    // Calculate number of courts with intelligent fallback
    const calculateCourts = () => {
        // First, try the explicit numberOfCourts field
        if (eventData.numberOfCourts) return parseInt(eventData.numberOfCourts);
        if (eventData.courts) return parseInt(eventData.courts);
        if (eventData.courtCount) return parseInt(eventData.courtCount);
        
        // Fallback: calculate from maxPairs (assuming 4 pairs per court)
        if (eventData.maxPairs) {
            const calculated = Math.ceil(parseInt(eventData.maxPairs) / 4);
            console.warn(`⚠️ numberOfCourts missing, calculated ${calculated} courts from maxPairs (${eventData.maxPairs})`);
            return calculated;
        }
        
        // Last resort default
        console.warn(`⚠️ numberOfCourts missing and no maxPairs, using default 4 courts`);
        return 4;
    };
    
    // Extract event details
    const eventName = eventData.title || "Torneo VisioPadel";
    const eventDate = eventData.date || new Date().toLocaleDateString('es-ES');
    const clubName = eventData.club?.name || "Club Padel";

    // State for Court Assignments
    const [assignments, setAssignments] = useState(() => {
        // Initialization: Use persisted assignments if they exist
        if (eventData.assignments && Object.keys(eventData.assignments).length > 0) {
            return eventData.assignments;
        }

        const totalCourts = calculateCourts();
        const initial = {};
        for (let i = 1; i <= totalCourts; i++) {
            initial[i] = { a1: null, a2: null, b1: null, b2: null };
        }
        return initial;
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);

    // Effect to update courts when numberOfCourts changes
    React.useEffect(() => {
        if (!eventData) return;
        const totalCourts = calculateCourts();
        
        setAssignments(prev => {
            const currentCount = Object.keys(prev).length;
            if (totalCourts === currentCount) return prev;

            const newAssignments = { ...prev };
            // Add missing courts
            for (let i = 1; i <= totalCourts; i++) {
                if (!newAssignments[i]) {
                    newAssignments[i] = { a1: null, a2: null, b1: null, b2: null };
                }
            }
            // Remove extra courts if count decreased
            const courtIds = Object.keys(newAssignments).map(Number);
            courtIds.forEach(id => {
                if (id > totalCourts) {
                    delete newAssignments[id];
                }
            });
            return newAssignments;
        });
    }, [eventData?.numberOfCourts, eventData?.courts, eventData?.courtCount, eventData?.maxPairs]);
    
    const [selectedSlot, setSelectedSlot] = useState(null); // { courtId, slotId }

    // --- DRAGGABLE SIDEBAR LOGIC ---
    const [sidebarPos, setSidebarPos] = useState({ x: window.innerWidth - 350, y: 100 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    
    // Handle initial responsive position
    React.useEffect(() => {
        const handleResize = () => {
             if (window.innerWidth >= 1024) {
                 setSidebarPos(prev => ({ 
                     x: Math.min(prev.x, window.innerWidth - 320),
                     y: prev.y 
                 }));
             }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - sidebarPos.x,
            y: e.clientY - sidebarPos.y
        });
    };

    const handleTouchStart = (e) => {
        setIsDragging(true);
        const touch = e.touches[0];
        setDragOffset({
            x: touch.clientX - sidebarPos.x,
            y: touch.clientY - sidebarPos.y
        });
    };

    const handleMouseMove = React.useCallback((e) => {
        if (isDragging) {
            setSidebarPos({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        }
    }, [isDragging, dragOffset]);

    const handleTouchMove = React.useCallback((e) => {
        if (isDragging) {
            const touch = e.touches[0];
            setSidebarPos({
                x: touch.clientX - dragOffset.x,
                y: touch.clientY - dragOffset.y
            });
            // Prevent scrolling while dragging on touch
            if (e.cancelable) e.preventDefault();
        }
    }, [isDragging, dragOffset]);

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    // Global listeners for drag events
    React.useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleDragEnd);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleDragEnd);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleDragEnd);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleDragEnd);
        };
    }, [isDragging, handleMouseMove, handleTouchMove]);
    // ----------------------------
    
    // We treat registrations as "Pairs" mostly.
    const pairs = registrations.filter(r => r.status === 'confirmed').map((r) => {
        // Use stored player names from registration
        const player1 = r.player1Name || 'Jugador sin nombre';
        const player2 = r.player2Name || r.partnerName || null;
        
        // Format the display name
        let displayName;
        if (player2) {
            displayName = `${player1} + ${player2}`;
        } else {
            displayName = player1;
        }
        
        return {
            id: r.id,
            name: displayName,
            type: player2 ? 'pair' : 'single',
            // Store raw names for match creation
            p1: player1,
            p2: player2,
            
            // Metadata for auto-assign
            level: r.level || r.playerLevel || 3, // Default level 3 if missing
            position: r.position || r.playerPosition || 'Ambos',
            hand: r.hand || r.playerHand || 'Diestro',
            
            // Pass full object for context
            ...r
        };
    });

    // Calculate which pairs are already assigned to prevent duplicates
    const assignedPairIds = React.useMemo(() => {
        const ids = new Set();
        Object.values(assignments).forEach(court => {
            Object.values(court).forEach(slot => {
                if (slot && slot.id) {
                    ids.add(slot.id);
                }
            });
        });
        return ids;
    }, [assignments]);

    // If slot is selected, show list of pairs to assign
    const handleAssign = (pair) => {
        if (!selectedSlot) return;
        const { courtId, slotId } = selectedSlot;
        
        setAssignments(prev => ({
            ...prev,
            [courtId]: { ...prev[courtId], [slotId]: pair }
        }));
        setSelectedSlot(null);
    };

    const handleClearSlot = (courtId, slotId, e) => {
        e.stopPropagation();
        setAssignments(prev => ({
            ...prev,
            [courtId]: { ...prev[courtId], [slotId]: null }
        }));
    };

    const handleSave = async () => {
        if (!onSaveAssignments) return;
        setIsSaving(true);
        try {
            await onSaveAssignments(assignments);
            toast.success("Pizarra guardada correctamente");
        } catch (err) {
            console.error("Save error:", err);
            toast.error("Error al guardar la pizarra");
        } finally {
            setIsSaving(false);
        }
    };

    // --- DRAG AND DROP LOGIC (Native HTML5) ---
    const [draggedItem, setDraggedItem] = useState(null); // { player, source: 'sidebar' | {courtId, slotId} }

    const handleDragStart = (e, player, source) => {
        setDraggedItem({ player, source });
        e.dataTransfer.effectAllowed = 'move';
        // Set a ghost image or data if needed, though default usually works
        // e.dataTransfer.setData('text/plain', JSON.stringify(player)); // Optional
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // Necessary to allow dropping
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, targetCourtId, targetSlotId) => {
        e.preventDefault();
        
        if (!draggedItem) return;

        const { player, source } = draggedItem;
        const targetCurrentPlayer = assignments[targetCourtId][targetSlotId];

        // 1. If dropping on the SAME slot, do nothing
        if (source.courtId === targetCourtId && source.slotId === targetSlotId) {
             setDraggedItem(null);
             return;
        }

        const newAssignments = { ...assignments };

        // 2. Logic based on Source
        if (source === 'sidebar') {
            // Assigning from Sidebar to Slot
            
            // If target has a player, we effectively "swapped" a sidebar player into a slot... 
            // BUT sidebar players are "copies" in the list until assigned. 
            // Actually, if we drop a sidebar player onto an occupied slot, we usually just REPLACE (overwrite).
            // The previous occupant goes back to "bench" (unassigned).
            
            // Check if player is already assigned somewhere else (prevent duplicates)
            // Note: assignedPairIds check is mostly for UI disabled state, but we should enforce here too
            // UNLESS we are moving the SAME player from sidebar (which shouldn't happen if disabled).
            // But if we drag an "Assigned" player from sidebar (if we allowed it), it would be weird.
            // Our sidebar disables dragging assigned players? We should ensure that.
            
            newAssignments[targetCourtId] = {
                ...newAssignments[targetCourtId],
                [targetSlotId]: player
            };
        } else {
            // Moving from On-Court Slot (Source) to Another Slot (Target)
            
            // Remove from Source Slot
            newAssignments[source.courtId] = {
                ...newAssignments[source.courtId],
                [source.slotId]: null // Temporarily empty source
            };

            // If Target had a player, SWAP them! (Put target player into source slot)
            if (targetCurrentPlayer) {
                 newAssignments[source.courtId][source.slotId] = targetCurrentPlayer;
            }

            // Assign dragged player to Target Slot
            newAssignments[targetCourtId] = {
                ...newAssignments[targetCourtId],
                [targetSlotId]: player
            };
        }

        setAssignments(newAssignments);
        setDraggedItem(null);
        toast.success(targetCurrentPlayer ? "Jugadores intercambiados" : "Jugador movido");
    };


    const handleSendToLive = async () => {
        if (!onInitializeMatches) return;
        
        // Filter courts that have ANY player assigned
        const courtsWithPlayers = Object.entries(assignments).filter(([_, slots]) => {
            return (slots.a1 || slots.a2) || (slots.b1 || slots.b2);
        });

        if (courtsWithPlayers.length === 0) {
            if (confirm("No hay jugadores asignados en ninguna pista. ¿Deseas limpiar todos los partidos activos en 'En Vivo'?")) {
                 setIsInitializing(true);
                 try {
                     await onInitializeMatches(assignments);
                     toast.success("Partidos limpiados");
                 } catch (err) {
                     console.error("Clear error:", err);
                     toast.error("Error al limpiar partidos");
                 } finally {
                     setIsInitializing(false);
                 }
            }
            return;
        }

        // VALIDATION: Ensure all active courts have full teams (2 vs 2)
        const incompleteCourts = [];
        
        courtsWithPlayers.forEach(([courtId, slots]) => {
            const countPlayers = (slot) => {
                if (!slot) return 0;
                // If it's a pair object (has p2 name or type='pair'), counts as 2. Otherwise 1.
                // Note: Logic in 'registrations' map sets type='pair' if player2Name exists.
                // We double check 'p2' property or 'player2Name' just in case.
                const isPair = slot.type === 'pair' || slot.p2 || slot.player2Name;
                return isPair ? 2 : 1;
            };

            const countA = countPlayers(slots.a1) + countPlayers(slots.a2);
            const countB = countPlayers(slots.b1) + countPlayers(slots.b2);

            if (countA !== 2 || countB !== 2) {
                incompleteCourts.push(`Pista ${courtId} (${countA} vs ${countB})`);
            }
        });

        if (incompleteCourts.length > 0) {
            toast.error(
                `No se puede iniciar: pistas incompletas.\n${incompleteCourts.join(', ')}\n\nSe requieren 4 jugadores (2 vs 2) por pista.`,
                { duration: 5000, style: { minWidth: '350px' } }
            );
            return;
        }

        if (!confirm(`¿Estás seguro? Se sincronizarán ${courtsWithPlayers.length} pistas con la gestión 'En Vivo'.`)) {
            return;
        }

        setIsInitializing(true);
        try {
            await onInitializeMatches(assignments);
            toast.success("Partidos sincronizados correctamente");
        } catch (err) {
            console.error("Initialize error:", err);
            toast.error("Error al sincronizar partidos");
        } finally {
            setIsInitializing(false);
        }
    };

    const canvasRef = useRef(null);


    // --- AUTO MATCHMAKING (Smart) ---
    const handleAutoAssign = () => {
        if (!confirm('¿Generar asignación automática? \n\nCada jugador será asignado a su posición óptima (Drive/Revés) y se marcará en la lista.')) return;

        // 1. Separar parejas fijas de individuales
        const fixedPairs = pairs.filter(p => p.type === 'pair');
        const singles = pairs.filter(p => p.type === 'single');

        // 2. Algoritmo de Emparejamiento Inteligente
        let formedPairs = []; // Array de { p1: obj, p2: obj, level: num, isFixed: false }
        
        // Ordenar individuales por nivel
        const sortedSingles = [...singles].sort((a, b) => (parseFloat(b.level) || 0) - (parseFloat(a.level) || 0));
        const unmatched = [...sortedSingles];

        while (unmatched.length >= 2) {
            const p1 = unmatched.shift();
            
            // Buscar pareja compatible (Lookahead 4)
            const searchWindow = 4; 
            const candidates = unmatched.slice(0, searchWindow);
            
            let bestMatchIndex = -1;
            let bestMatchScore = -999;

            candidates.forEach((p2, idx) => {
                let score = 0;
                // Nivel
                const levelDiff = Math.abs((parseFloat(p1.level) || 0) - (parseFloat(p2.level) || 0));
                score -= levelDiff * 2;
                // Posición
                const pos1 = p1.position || 'Ambos';
                const pos2 = p2.position || 'Ambos';
                if ((pos1 === 'Drive' && pos2 === 'Revés') || (pos1 === 'Revés' && pos2 === 'Drive')) score += 5; 
                else if (pos1 === 'Ambos' || pos2 === 'Ambos') score += 2;
                else if (pos1 === pos2) score -= 5;
                // Lateralidad
                if ((p1.hand || 'Diestro') !== (p2.hand || 'Diestro')) score += 1;

                if (score > bestMatchScore) {
                    bestMatchScore = score;
                    bestMatchIndex = idx;
                }
            });

            let p2;
            if (bestMatchIndex !== -1) {
                p2 = unmatched.splice(bestMatchIndex, 1)[0];
            } else {
                p2 = unmatched.shift();
            }

            // DETERMINAR POSICIÓN EN PISTA (A1=Revés, A2=Drive)
            // Lógica: Si uno es Revés puro, va a A1. Si uno es Drive puro, va A2.
            let playerReves = p1;
            let playerDrive = p2;

            const pos1 = p1.position;
            const pos2 = p2.position;

            if (pos2 === 'Revés' && pos1 !== 'Revés') {
                playerReves = p2;
                playerDrive = p1;
            } else if (pos1 === 'Drive' && pos2 !== 'Drive') {
                // p1 es Drive, mejor que vaya a A2 (Drive) -> p2 a Revés
                playerReves = p2;
                playerDrive = p1;
            }
            
            formedPairs.push({
                isFixed: false,
                p1: playerReves,
                p2: playerDrive,
                level: ((parseFloat(p1.level)||0) + (parseFloat(p2.level)||0)) / 2
            });
        }

        if (unmatched.length > 0) {
            toast('⚠️ Jugadores impares: algunos se quedarán sin asignar.', { icon: '⚠️' });
        }

        // 3. Preparar lista unificada de unidades para asignar (Parejas Fijas + Formadas)
        let allUnits = [
            ...fixedPairs.map(p => ({ isFixed: true, data: p, level: p.level || 3 })),
            ...formedPairs
        ];

        // 4. Ordenar por Nivel (Mejores a Pista 1)
        allUnits.sort((a, b) => b.level - a.level);

        // 5. Asignar a Pistas
        const newAssignments = {};
        const courtIds = Object.keys(assignments).sort((a, b) => parseInt(a) - parseInt(b));
        
        let unitIdx = 0;

        courtIds.forEach(courtId => {
            newAssignments[courtId] = { a1: null, a2: null, b1: null, b2: null };
            
            // Asignar Equipo A (Slot A1 y A2)
            if (unitIdx < allUnits.length) {
                const unit = allUnits[unitIdx++];
                if (unit.isFixed) {
                    newAssignments[courtId].a1 = unit.data; 
                    // a2 null - Pareja Fija ocupa 1 slot visualmente pero vale por 2
                } else {
                    newAssignments[courtId].a1 = unit.p1; // Revés
                    newAssignments[courtId].a2 = unit.p2; // Drive
                }
            }
            
            // Asignar Equipo B (Slot B1 y B2)
            if (unitIdx < allUnits.length) {
                const unit = allUnits[unitIdx++];
                if (unit.isFixed) {
                    newAssignments[courtId].b1 = unit.data;
                } else {
                    newAssignments[courtId].b1 = unit.p1; // Revés
                    newAssignments[courtId].b2 = unit.p2; // Drive
                }
            }
        });

        setAssignments(newAssignments);
        toast.success(`¡Asignación Inteligente Completada!`);
    };

    const handleExport = async () => {
        if (!canvasRef.current) return;
        try {
            const canvas = await html2canvas(canvasRef.current, {
                scale: 3, // Higher resolution
                backgroundColor: "#111827", // Force dark bg
                useCORS: true, // Essential for images
                logging: false,
            });
            const image = canvas.toDataURL("image/png");
            
            // Trigger download
            const link = document.createElement("a");
            link.href = image;
            link.download = `OrdenJuego-${eventName.replace(/\s+/g, '-')}.png`;
            link.click();
            toast.success("Imagen generada correctamente");
        } catch (err) {
            console.error("Export Error:", err);
            toast.error("Error al generar imagen");
        }
    };

    return (
        <div className="space-y-8 relative">
            {/* ... (Top Controls - No Change) */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-black text-gray-900">Pizarra de Pistas</h2>
                    <p className="text-gray-500 text-sm">Organiza los partidos y comparte la imagen.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button 
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-blue-100 text-blue-700 font-bold py-2 px-4 rounded-xl flex items-center gap-2 hover:bg-blue-200 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? 'Guardando...' : 'Guardar Pizarra'}
                    </button>
                    <button 
                        onClick={handleAutoAssign}
                        className="bg-purple-600 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 hover:bg-purple-700 transition-colors shadow-lg shadow-purple-500/20"
                    >
                        ⚡ Auto
                    </button>
                    <button 
                        onClick={handleSendToLive}
                        disabled={isInitializing}
                        className="bg-primary-500 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/20 disabled:opacity-50"
                    >
                        {isInitializing ? 'Iniciando...' : '🚀 Enviar a Live'}
                    </button>
                    <button 
                        onClick={handleExport}
                        className="bg-green-600 text-white font-bold py-2 px-4 rounded-xl flex items-center gap-2 hover:bg-green-700 transition-colors shadow-lg"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        WhatsApp
                    </button>
                </div>
            </div>

            {/* Main Content Area - Full Width now */}
            <div className="relative">
                
                {/* Visual Court Grid (The Canvas) */}
                <div className="w-full overflow-x-auto bg-gray-900 rounded-3xl p-4 md:p-8 border border-gray-800 shadow-2xl relative mb-20 lg:mb-0">
                   <div ref={canvasRef} className="bg-[#111827] p-4 md:p-8 rounded-xl text-white min-w-[500px] md:min-w-[600px] relative overflow-hidden">
                        {/* Decorative Background Elements */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none"></div>

                        {/* Detailed Header */}
                        <div className="text-center mb-6 md:mb-10 relative z-10">
                            <div className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 mb-4 backdrop-blur-sm">
                                <p className="text-primary-400 font-bold uppercase text-[10px] tracking-[0.3em]">
                                    Orden de Juego Oficial
                                </p>
                            </div>
                            
                            <h1 className="text-2xl md:text-4xl font-black text-white uppercase tracking-wide mb-2 drop-shadow-lg">
                                {eventName}
                            </h1>
                            
                            <div className="flex items-center justify-center gap-4 md:gap-6 text-gray-400 text-[10px] md:text-xs font-medium uppercase tracking-widest mt-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-primary-500 text-base md:text-lg">📅</span> {eventDate}
                                </div>
                                <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                                <div className="flex items-center gap-2">
                                    <span className="text-blue-500 text-base md:text-lg">📍</span> {clubName}
                                </div>
                            </div>
                            
                            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto mt-6 opacity-50"></div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 relative z-10">
                            {Object.entries(assignments).map(([courtId, slots]) => (
                                <div key={courtId} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4 relative backdrop-blur-sm">
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-1 rounded-full text-xs font-black uppercase tracking-wider shadow-lg border border-blue-400/30 text-white z-20">
                                        Pista {courtId}
                                    </div>
                                    
                                    {/* Padel Court Design */}
                                    <div className="relative bg-[#4a7ec1] aspect-[20/10] rounded-lg border-[3px] border-white/90 shadow-inner flex overflow-hidden mt-2">
                                        
                                        {/* Left Side (Pareja 2) - Back Wall is Left */}
                                        <div className="flex-1 relative flex flex-col justify-center">
                                            {/* Service Line (Vertical): 3m from back wall (approx 30%) */}
                                            <div className="absolute top-0 bottom-0 left-[30%] border-r-2 border-white w-px h-full z-0"></div>
                                            
                                            {/* Center Line (Horizontal): From Service Line (30%) to Net (100%) */}
                                            <div className="absolute top-1/2 left-[30%] right-0 border-t-2 border-white h-px z-0"></div>

                                            {/* Player Slots Container - Centered in Service Boxes (Right 70%) */}
                                            <div className="relative z-10 w-full h-full flex flex-col pl-[30%]">
                                                {/* Top Half (Reves?) */}
                                                <div className="flex-1 flex items-center justify-center p-2">
                                                    <Slot 
                                                        key={`${courtId}-b1-${slots.b1?.id || 'empty'}`}
                                                        label="B1" 
                                                        value={slots.b1} 
                                                        onClick={() => setSelectedSlot({ courtId, slotId: 'b1' })}
                                                        onClear={(e) => handleClearSlot(courtId, 'b1', e)}
                                                        isActive={selectedSlot?.courtId === courtId && selectedSlot?.slotId === 'b1'}
                                                        onDragStart={(e) => handleDragStart(e, slots.b1, { courtId, slotId: 'b1' })}
                                                        onDragOver={handleDragOver}
                                                        onDrop={(e) => handleDrop(e, courtId, 'b1')}
                                                    />
                                                </div>
                                                {/* Bottom Half (Drive?) */}
                                                <div className="flex-1 flex items-center justify-center p-2">
                                                    <Slot 
                                                        key={`${courtId}-b2-${slots.b2?.id || 'empty'}`}
                                                        label="B2" 
                                                        value={slots.b2} 
                                                        onClick={() => setSelectedSlot({ courtId, slotId: 'b2' })}
                                                        onClear={(e) => handleClearSlot(courtId, 'b2', e)}
                                                        isActive={selectedSlot?.courtId === courtId && selectedSlot?.slotId === 'b2'}
                                                        onDragStart={(e) => handleDragStart(e, slots.b2, { courtId, slotId: 'b2' })}
                                                        onDragOver={handleDragOver}
                                                        onDrop={(e) => handleDrop(e, courtId, 'b2')}
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Team Label */}
                                            <div className="absolute left-2 top-1/2 transform -translate-y-1/2 -rotate-90 text-white/50 text-[10px] font-bold tracking-widest pointer-events-none">
                                                PAREJA 2
                                            </div>
                                        </div>

                                        {/* Net */}
                                        <div className="w-1.5 bg-gray-200 relative z-20 shadow-xl flex items-center justify-center h-full">
                                            {/* Net Pattern */}
                                            <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjMzMzIiBmaWxsLW9wYWNpdHk9IjAuMiIvPjwvc3ZnPg==')]"></div>
                                        </div>

                                        {/* Right Side (Pareja 1) - Back Wall is Right */}
                                        <div className="flex-1 relative flex flex-col justify-center">
                                            {/* Service Line (Vertical): 3m from back wall (approx 30% from right) */}
                                            <div className="absolute top-0 bottom-0 right-[30%] border-l-2 border-white w-px h-full z-0"></div>
                                            
                                            {/* Center Line (Horizontal): From Net (0%) to Service Line (right 30%) */}
                                            <div className="absolute top-1/2 left-0 right-[30%] border-t-2 border-white h-px z-0"></div>

                                            {/* Player Slots Container - Centered in Service Boxes (Left 70%) */}
                                            <div className="relative z-10 w-full h-full flex flex-col pr-[30%]">
                                                 {/* Top Half */}
                                                 <div className="flex-1 flex items-center justify-center p-2">
                                                    <Slot 
                                                        key={`${courtId}-a1-${slots.a1?.id || 'empty'}`}
                                                        label="A1" 
                                                        value={slots.a1} 
                                                        onClick={() => setSelectedSlot({ courtId, slotId: 'a1' })}
                                                        onClear={(e) => handleClearSlot(courtId, 'a1', e)}
                                                        isActive={selectedSlot?.courtId === courtId && selectedSlot?.slotId === 'a1'}
                                                        onDragStart={(e) => handleDragStart(e, slots.a1, { courtId, slotId: 'a1' })}
                                                        onDragOver={handleDragOver}
                                                        onDrop={(e) => handleDrop(e, courtId, 'a1')}
                                                    />
                                                </div>
                                                {/* Bottom Half */}
                                                <div className="flex-1 flex items-center justify-center p-2">
                                                    <Slot 
                                                        key={`${courtId}-a2-${slots.a2?.id || 'empty'}`}
                                                        label="A2" 
                                                        value={slots.a2} 
                                                        onClick={() => setSelectedSlot({ courtId, slotId: 'a2' })}
                                                        onClear={(e) => handleClearSlot(courtId, 'a2', e)}
                                                        isActive={selectedSlot?.courtId === courtId && selectedSlot?.slotId === 'a2'}
                                                        onDragStart={(e) => handleDragStart(e, slots.a2, { courtId, slotId: 'a2' })}
                                                        onDragOver={handleDragOver}
                                                        onDrop={(e) => handleDrop(e, courtId, 'a2')}
                                                    />
                                                </div>
                                            </div>

                                            {/* Team Label */}
                                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 rotate-90 text-white/50 text-[10px] font-bold tracking-widest pointer-events-none">
                                                PAREJA 1
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="text-center mt-8 text-[10px] text-gray-600 font-mono">
                            Generado por VisioPadel
                        </div>
                   </div>
                </div>

                {/* Draggable Sidebar: Roster (Interactive) */}
                <div 
                    className={`fixed bg-white rounded-2xl border border-gray-100 shadow-2xl w-80 max-h-[70vh] flex flex-col transition-shadow duration-300 z-[100] hidden md:flex ${isDragging ? 'cursor-grabbing shadow-2xl scale-[1.02]' : 'cursor-grab'} ${selectedSlot ? 'ring-4 ring-primary-500/50' : ''}`}
                    style={{ 
                        left: `${sidebarPos.x}px`, 
                        top: `${sidebarPos.y}px`,
                    }}
                >
                    {/* Header - Draggable Handle */}
                    <div 
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                        className="p-4 bg-gray-50 border-b border-gray-100 rounded-t-2xl flex justify-between items-center select-none"
                    >
                        <h3 className="font-bold text-gray-900 flex items-center gap-2">
                             <span>:::</span> 
                             {selectedSlot ? (
                                <span className="text-primary-600">Selecciona Jugador</span>
                            ) : (
                                <span>Inscritos ({pairs.length})</span>
                            )}
                        </h3>
                        <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                    </div>

                    <div className="p-2 bg-gray-50 border-b border-gray-100">
                        {selectedSlot ? (
                            <p className="text-[10px] text-center font-bold text-primary-600 animate-pulse">
                                Asignando a Pista {selectedSlot.courtId}
                            </p>
                        ) : (
                            <p className="text-[10px] text-center text-gray-400">
                                💡 Arrastra esta ventana donde quieras
                            </p>
                        )}
                    </div>

                    <div className="p-4 overflow-y-auto custom-scrollbar flex-1 bg-white rounded-b-2xl cursor-default" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="space-y-2">
                            {pairs.map(pair => {
                                const isAssigned = assignedPairIds.has(pair.id);
                                return (
                                    <button
                                        key={pair.id}
                                        draggable={!isAssigned} // Only draggable if not assigned
                                        onDragStart={(e) => handleDragStart(e, pair, 'sidebar')}
                                        onClick={() => (!isAssigned && selectedSlot) ? handleAssign(pair) : null}
                                        disabled={!selectedSlot && !(!selectedSlot) && isAssigned} // Revert logic: disabled logic was tricky. 
                                        // Simplified: Always clickable if not assigned (to set logic), 
                                        // but if we drag, we don't click.
                                        // The original logic was: disabled={!selectedSlot || isAssigned}
                                        // BUT now we want to DRAG even if no slot selected.
                                        // SO disabled should be FALSE for dragging purposes? Be careful.
                                        // If button is disabled, draggable might not work in some browsers.
                                        // Better to use a div or keep button enabled but handle click.
                                        // Let's keep button but remove 'disabled' attribute for unassigned, just style it.
                                        className={`w-full text-left p-3 rounded-lg text-xs font-medium transition-all flex justify-between items-center group
                                            ${isAssigned 
                                                ? 'bg-gray-50 border border-transparent text-gray-400 cursor-not-allowed opacity-60' 
                                                : 'bg-white border border-gray-200 hover:border-primary-500 hover:shadow-md cursor-grab active:cursor-grabbing text-gray-800'
                                            }
                                        `}
                                    >
                                        <span className={`truncate mr-2 ${isAssigned ? 'line-through decoration-gray-400' : ''}`}>{pair.name}</span>
                                        {isAssigned ? (
                                             <span className="text-[9px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border border-gray-300">
                                                En Pista
                                            </span>
                                        ) : (
                                            selectedSlot && (
                                                <span className="opacity-0 group-hover:opacity-100 text-[10px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                                                    Asignar
                                                </span>
                                            )
                                        )}
                                    </button>
                                );
                            })}
                            
                            {pairs.length === 0 && (
                                <div className="text-center py-8 text-gray-400 italic text-xs">
                                    No hay jugadores inscritos
                                </div>
                            )}
                        </div>
                        
                        {selectedSlot && (
                            <button 
                                onClick={() => setSelectedSlot(null)}
                                className="mt-4 w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors"
                            >
                                Cancelar Selección
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal removed as requested */}
            {selectedSlot && window.innerWidth < 768 && (
                // Mobile Fallback: Keep a simplified modal ONLY for small screens where sidebar is hidden
                 <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setSelectedSlot(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                             <h3 className="font-bold text-gray-900">Seleccionar Jugador</h3>
                             <button onClick={() => setSelectedSlot(null)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                             </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 space-y-2">
                             {pairs.map(pair => {
                                 const isAssigned = assignedPairIds.has(pair.id);
                                 return (
                                    <button 
                                        key={pair.id} 
                                        onClick={() => !isAssigned && handleAssign(pair)} 
                                        disabled={isAssigned}
                                        className={`w-full text-left p-4 rounded-xl border text-sm font-bold transition-colors flex justify-between items-center
                                            ${isAssigned 
                                                ? 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed' 
                                                : 'bg-white border-gray-100 hover:bg-primary-50 hover:border-primary-200 text-gray-800'
                                            }
                                        `}
                                    >
                                        <span className={isAssigned ? 'line-through' : ''}>{pair.name}</span>
                                        {isAssigned && <span className="text-xs uppercase bg-gray-200 px-2 py-0.5 rounded">Ocupado</span>}
                                    </button>
                                 );
                             })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Slot = ({ label, value, onClick, onClear, isActive, onDragStart, onDragOver, onDrop }) => (
    <div 
        onClick={onClick}
        onDragOver={onDragOver}
        onDrop={onDrop}
        draggable={!!value}
        onDragStart={onDragStart}
        className={`relative group transition-all duration-300 hover:scale-105 cursor-pointer w-full flex items-center justify-center
            ${isActive ? 'scale-110 z-20' : 'z-10'}
        `}
    >
        {/* Simplified Padel Ball - Responsive Size */}
        <div className={`
            w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center relative
            ${value ? 'bg-[#d4ff00] border-2 md:border-4 border-[#b8e600] shadow-xl hover:bg-[#e0ff4d]' : 'bg-gray-800/50 border-2 border-dashed border-gray-600 hover:border-gray-500 hover:bg-gray-800/80'}
            ${isActive ? 'ring-2 md:ring-4 ring-blue-500' : ''}
            transition-colors duration-200
        `}
        >
            {value ? (
                <>
                    {/* Simple curved line for seam */}
                    <div className="absolute inset-0 flex items-center justify-center rotate-45 pointer-events-none">
                        <div className="w-full h-0 border-t md:border-t-2 border-white opacity-60"></div>
                    </div>

                    {/* Player Name - Scales with ball */}
                    <div className="relative z-20 text-center w-full px-0.5">
                        <div className="font-black text-[7px] md:text-[9px] lg:text-[11px] leading-tight uppercase text-gray-900" 
                             style={{ 
                                 wordBreak: 'break-word',
                                 hyphens: 'auto',
                                 lineHeight: '1.1'
                             }}>
                            {value.name}
                        </div>
                    </div>

                    <button 
                        onClick={onClear}
                        className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 bg-red-500 text-white w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center text-[8px] md:text-xs opacity-0 group-hover:opacity-100 transition-opacity z-50 border md:border-2 border-white"
                    >
                        ×
                    </button>
                </>
            ) : (
                <span className="font-black text-[8px] md:text-xs select-none text-white/30">{label}</span>
            )}
        </div>
    </div>
);

export default CourtManager;
